import type { Aggregation } from './aggregate.ts'

/**
 * Derived series for the cases where two measures sit almost on top of each
 * other: three overlaid lines hide a 2% gap, a plot of the gap itself does not.
 *
 * Every function returns one value per bucket, aligned with
 * `agg.bucketStarts`, or `null` for the whole result when an input series is
 * not available for this controller. `NaN` marks a bucket the ratio is
 * undefined for (nothing flowed), which the charts draw as a gap.
 */

function series(agg: Aggregation, key: string): number[] | null {
  return agg.series.find((s) => s.key === key)?.buckets ?? null
}

/** `minuend − Σ subtrahends`, per bucket. */
export function difference(agg: Aggregation, minuendKey: string, subtrahendKeys: string[]): number[] | null {
  const a = series(agg, minuendKey)
  const subs = subtrahendKeys.map((k) => series(agg, k))
  if (!a || subs.some((s) => s === null)) return null
  return a.map((v, i) => subs.reduce((acc, s) => acc - (s![i] ?? 0), v))
}

/** `part / Σ wholes × 100`, per bucket; NaN where nothing flowed. */
export function share(agg: Aggregation, partKey: string, wholeKeys: string[]): number[] | null {
  const part = series(agg, partKey)
  const wholes = wholeKeys.map((k) => series(agg, k))
  if (!part || wholes.some((s) => s === null)) return null
  return part.map((p, i) => {
    const whole = wholes.reduce((acc, s) => acc + (s![i] ?? 0), 0)
    return whole > 0 ? (p / whole) * 100 : NaN
  })
}

/** Sum of the defined values — the net figure a difference chart leads with. */
export function net(values: number[]): number {
  return values.reduce((acc, v) => (Number.isNaN(v) ? acc : acc + v), 0)
}
